import React, { useEffect } from 'react';
import { arrayOf, shape, string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { NamedLink } from '../../components';
import {
  fetchCategoryCounts,
  keyFromParams,
} from '../../ducks/categoryCounts.duck';
import catFalgar from '../../assets/cat-falgar.avif';
import catSommardack from '../../assets/cat-sommardack.avif';
import catSommarhjul from '../../assets/cat-sommarhjul.avif';
import catVinterdack from '../../assets/cat-vinterdack.avif';
import catVinterhjulDubb from '../../assets/cat-vinterhjul-dubb.avif';
import catVinterhjulFriktion from '../../assets/cat-vinterhjul-friktion.avif';
import css from './CategoryGrid.module.css';

// Category tiles map to real listing filter params (pub_adType + pub_tiretype).
// adType values — 'tire', 'rim', 'wheel' — match the EditListingDetails form.
// tiretype values are the enum option slugs defined in Sharetribe Console; if
// these don't match your config, update the `params` here accordingly.
// Counts are fetched live from the SDK (see categoryCounts.duck) — no more
// hardcoded numbers.
const DEFAULT_CATEGORIES = [
  {
    id: 'vinterhjul-friktion',
    label: 'Vinterhjul friktion',
    image: catVinterhjulFriktion,
    params: { pub_adType: 'wheel', pub_tiretype: 'winterFriction' },
  },
  {
    id: 'vinterhjul-dubb',
    label: 'Vinterhjul dubb',
    image: catVinterhjulDubb,
    params: { pub_adType: 'wheel', pub_tiretype: 'winterDubbed' },
  },
  {
    id: 'vinterdack',
    label: 'Vinterdäck',
    image: catVinterdack,
    params: { pub_adType: 'tire', pub_tiretype: 'winterFriction,winterDubbed' },
  },
  {
    id: 'falgar',
    label: 'Fälgar',
    image: catFalgar,
    params: { pub_adType: 'rim' },
  },
  {
    id: 'sommarhjul',
    label: 'Sommarhjul',
    image: catSommarhjul,
    params: { pub_adType: 'wheel', pub_tiretype: 'summer' },
  },
  {
    id: 'sommardack',
    label: 'Sommardäck',
    image: catSommardack,
    params: { pub_adType: 'tire', pub_tiretype: 'summer' },
  },
];

const buildSearch = params =>
  Object.entries(params)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

const formatCount = n => {
  if (n === 1) return '1 annons';
  return `${n} annonser`;
};

const CategoryGrid = ({ categories = DEFAULT_CATEGORIES }) => {
  const dispatch = useDispatch();
  const countsMap = useSelector(state => state.categoryCounts?.counts || {});

  // Fetch on mount. The thunk internally dedupes — re-dispatching with the
  // same params won't re-fire the network once counts are cached.
  useEffect(() => {
    dispatch(fetchCategoryCounts({ categories }));
  }, [dispatch, categories]);

  return (
    <section className={css.root}>
      <div className={css.grid}>
        {categories.map(cat => {
          const count = countsMap[keyFromParams(cat.params || {})];
          return (
            <NamedLink
              key={cat.id}
              name="SearchPage"
              to={{ search: `?${buildSearch(cat.params || {})}` }}
              className={css.card}
            >
              <div className={css.imageWrap}>
                <img src={cat.image} alt={cat.label} className={css.image} loading="lazy" />
              </div>
              <div className={css.meta}>
                <span className={css.label}>{cat.label}</span>
                {typeof count === 'number' ? (
                  <span className={css.count}>{formatCount(count)}</span>
                ) : null}
              </div>
            </NamedLink>
          );
        })}
      </div>
    </section>
  );
};

CategoryGrid.propTypes = {
  categories: arrayOf(
    shape({
      id: string.isRequired,
      label: string.isRequired,
      image: string.isRequired,
      params: shape({}),
    })
  ),
};

export default CategoryGrid;
